'use strict';

import BaseLogger from './base-logger';
import BiLoadFinishDataItem from './data-items/bi-load-finish-item';
import DurationDataItem from './data-items/duration-item';
import BiInteractionStartDataItem from './data-items/bi-interaction-start-item';
import BiInteractionEndDataItem from './data-items/bi-interaction-end-item';
import BiErrorItem from './data-items/bi-error-item';
import performance from './performance/performance';

const callHook = (hook, hookArgs) => {
  if (hook) {
    return hook(hookArgs);
  }
};

/**
 * Logs application load phases.
 *
 * Before adding new parameters to the reported events, please make sure the BI schema supports them:
 * http://bo.wix.com/bi-catalog-webapp/#/sources/72
 */
export default class LoggerInstance extends BaseLogger {
  constructor(appName, appVersion, reporter, {sessionId, errorHandlerToggle, disableAutoLoadFinish, interactionTimeout, timeoutHook, startHook, endHook}) {
    super(appName, appVersion, reporter, {sessionId, errorHandlerToggle, disableAutoLoadFinish});

    this._outgoingInteractions = {};
    this.interactionTimeout = interactionTimeout;
    this.timeoutHook = timeoutHook;
    this.startHook = startHook;
    this.endHook = endHook;
  }

  /**
   * Final phase (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/11)
   * Call this method when your application finishes loading and after all custom phases.
   */
  appLoaded() {
    if (this._appLoadedCalled) {
      return;
    }

    if (window && window.fedops && typeof window.fedops.clearLoadTimeout === 'function') {
      window.fedops.clearLoadTimeout();
    }

    const dataSource = this.dataSource.clone()
      .addItem(new BiLoadFinishDataItem(this.sessionId))
      .addItem(new DurationDataItem(this.appName).setFirstRequestDuration());

    this._appLoadedCalled = true;
    performance.mark(`[fedops] app-loaded`);
    return this._report(dataSource);
  }

  /**
   * Custom interaction start (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/15)
   *
   * @param {String} interactionName  interaction name
   */
  interactionStarted(interactionName, {interactionTimeout, startHook, timeoutHook} = {}) {
    performance.mark(`${interactionName} started`);
    const scheduleTimeoutEvent = () => {
      if (interactionTimeout || this.interactionTimeout) {
        return setTimeout(() => {
          const dataSource = this.dataSource.clone()
            .addItem({interactionName})
            .addItem({errorType: 'timeout'})
            .addItem(new BiErrorItem(this.sessionId));

          this._report(dataSource);
          callHook(timeoutHook || this.timeoutHook, {name: interactionName, timeout: (interactionTimeout || this.interactionTimeout)});
        }, interactionTimeout || this.interactionTimeout);
      }
    };
    const dataSource = this.dataSource.clone()
      .addItem({name: interactionName})
      .addItem(new BiInteractionStartDataItem(this.sessionId));

    this._outgoingInteractions[interactionName] = {
      timestamp: performance.now(),
      timeout: interactionTimeout || this.interactionTimeout
    };
    this._report(dataSource);
    callHook(startHook || this.startHook, {name: interactionName});

    const timeoutId = scheduleTimeoutEvent();
    return {
      timeoutId
    };
  }

  /**
   * Custom interaction end (http://bo.wix.com/bi-catalog-webapp/#/sources/72/events/16)
   *
   * @param {String} interactionName  interaction name
   */
  interactionEnded(interactionName, {timeoutId, endHook} = {}) {
    performance.mark(`${interactionName} ended`);
    performance.measure(`${interactionName} duration`, `${interactionName} started`, `${interactionName} ended`);

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const duration = this._outgoingInteractions[interactionName] ?
      Math.floor(performance.now() - this._outgoingInteractions[interactionName].timestamp) :
      '';

    callHook(endHook || this.endHook, {
      name: interactionName,
      duration,
      timeout: this._outgoingInteractions[interactionName] && this._outgoingInteractions[interactionName].timeout
    });

    const dataSource = this.dataSource.clone()
      .addItem({name: interactionName})
      .addItem({duration})
      .addItem(new BiInteractionEndDataItem(this.sessionId));

    delete this._outgoingInteractions[interactionName];

    this._report(dataSource);
  }

  isDisableAutoLoadFinish() {
    return this._isDisableAutoLoadFinish();
  }
}

// vim: set ts=2 sw=2 tw=80 et :



// WEBPACK FOOTER //
// ./logger-instance.js